'use client';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  keyframes,
} from '@mui/material';
import { AdvancedLoginIllustration } from './AdvanceLogin';

const floatIn = keyframes`
  0% { opacity: 0; transform: translateY(-30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const blink = keyframes`
  0%, 50% { opacity: 0; }
  51%, 100% { opacity: 1; }
`;

const handTap = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-5px) rotate(-10deg); }
`;

export const AnimatedLoginSequence: React.FC = () => {
  type Stage = 'inputs' | 'click' | 'lock' | 'success';
  const [stage, setStage] = useState<Stage>('inputs');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    const USER = 'john_doe@email.com';
    const PASS = '••••••';

    // timing constants (ms)
    const startDelay = 500;
    const userTypeTime = USER.length * 100 + 200;
    const passTypeTime = PASS.length * 100 + 300;
    const clickTime = 1000;
    const lockTime = 5000;
    const successHold = 2000;
    const totalCycle =
      startDelay +
      userTypeTime +
      passTypeTime +
      clickTime +
      lockTime +
      successHold;

    const runSequence = () => {
      // reset
      setStage('inputs');
      setUsername('');
      setPassword('');

      // 1) type USER
      USER.split('').forEach((ch, i) => {
        timers.push(
          setTimeout(() => setUsername((s) => s + ch), startDelay + i * 100),
        );
      });

      // 2) type PASS
      PASS.split('').forEach((ch, i) => {
        timers.push(
          setTimeout(
            () => setPassword((s) => s + ch),
            startDelay + userTypeTime + i * 100,
          ),
        );
      });

      // 3) show “Click”
      timers.push(
        setTimeout(
          () => setStage('click'),
          startDelay + userTypeTime + passTypeTime,
        ),
      );

      // 4) show “Lock”
      timers.push(
        setTimeout(
          () => setStage('lock'),
          startDelay + userTypeTime + passTypeTime + clickTime,
        ),
      );

      // 5) show “Success”
      timers.push(
        setTimeout(
          () => setStage('success'),
          startDelay + userTypeTime + passTypeTime + clickTime + lockTime,
        ),
      );

      // 6) loop again
      timers.push(setTimeout(() => runSequence(), totalCycle));
    };

    runSequence();

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* 1) Inputs */}
      {stage === 'inputs' && (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 3,
            bgcolor: '#ffffff',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            boxShadow:
              '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
            animation: `${floatIn} 0.8s ease-out forwards`,
          }}
        >
          <TextField
            size="small"
            placeholder="Username"
            value={username}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Box
                    sx={{
                      width: 2,
                      height: 18,
                      bgcolor: '#059669',
                      animation: `${blink} 1s step-end infinite`,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                bgcolor: '#f8fafc',
                '& fieldset': {
                  borderColor: '#cbd5e1',
                },
                '&:hover fieldset': {
                  borderColor: '#94a3b8',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#059669',
                },
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '0.875rem',
                color: '#1e293b',
              },
            }}
          />

          <TextField
            size="small"
            type="password"
            placeholder="Password"
            value={password}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Box
                    sx={{
                      width: 2,
                      height: 18,
                      bgcolor: '#059669',
                      animation: `${blink} 1s step-end infinite`,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                bgcolor: '#f8fafc',
                '& fieldset': {
                  borderColor: '#cbd5e1',
                },
                '&:hover fieldset': {
                  borderColor: '#94a3b8',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#059669',
                },
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '0.875rem',
                color: '#1e293b',
              },
            }}
          />
        </Paper>
      )}

      {/* 2) Click */}
      {stage === 'click' && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ animation: `${fadeIn} 0.8s ease-out forwards` }}
        >
          <Button
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              bgcolor: '#093725',
              color: '#ffffff',
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: '0 4px 12px rgba(9, 55, 37, 0.25)',
              '&:hover': {
                bgcolor: '#06281b',
              },
            }}
          >
            Login
          </Button>
          <Box
            component="svg"
            viewBox="0 0 64 64"
            sx={{
              width: 32,
              height: 32,
              animation: `${handTap} 0.6s ease-in-out infinite`,
            }}
          >
            <path
              d="M20,10 L20,40 C20,50 28,58 38,58 48,58 56,50 56,40 56,30 48,22 38,22 34,22 31,19 31,10 25,6 20,10 Z"
              fill="#94a3b8"
            />
          </Box>
        </Stack>
      )}

      {/* 3) Lock */}
      {stage === 'lock' && (
        <Box
          sx={{
            borderRadius: '6px',
            animation: `${fadeIn} 0.8s ease-out forwards`,
          }}
        >
          <AdvancedLoginIllustration />
        </Box>
      )}

      {/* 4) Success */}
      {stage === 'success' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            animation: `${fadeIn} 0.8s ease-out forwards`,
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 100 100"
            sx={{
              width: 96,
              height: 96,
              mb: 2,
            }}
          >
            <circle cx="50" cy="50" r="45" fill="#10B981" />
            <path
              d="M30,52 L45,68 L70,35"
              stroke="#ffffff"
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Box>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#059669',
              letterSpacing: '-0.01em',
            }}
          >
            Login Successful!
          </Typography>
        </Box>
      )}
    </Box>
  );
};
